'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import dynamic from 'next/dynamic';
import type { ReactNode } from 'react';
import { resolveNavigation, resolveActiveContext } from '@/src/lib/navigation';
import { useTheme } from '@/src/lib/use-theme';
import { isPublicUiPath } from '@/src/lib/route-access';
import type { UiContext } from '@/src/server/ui-context/ui-context.helper';
import { AppSidebar } from './sidebar';
import { Topbar } from './topbar';
import { MobileBottomNav } from './mobile-bottom-nav';
import { NavTracker } from './nav-tracker';
import { PowerModeWorkflowsBanner } from '@/src/components/onboarding/power-mode-workflows-banner';
import { ErrorBoundary } from '@/src/components/ui-kontora/error-boundary';
import { ToastContainer } from '@/src/components/ui-kontora/toast-container';
import { GlobalConfirmationProvider } from '@/src/components/ui-kontora/global-confirmation-provider';
import { configureBufferingSink } from '@/src/lib/onboarding/telemetry-buffer';
import { AppShellCommandPalette } from '@/src/components/command-palette/app-shell-command-palette';
import { SearchPanelMonitor } from '@/src/components/search/search-panel-monitor';
import { SessionTimeoutMonitor } from '@/src/components/app-shell/session-timeout-monitor';
import { ShortcutsCheatsheetMonitor } from '@/src/components/shortcuts/shortcuts-cheatsheet-monitor';
import { PwaRegister } from '@/src/components/pwa/pwa-register';
import { InstallPrompt } from '@/src/components/pwa/install-prompt';
import { BrandingProvider } from '@/src/components/branding/branding-provider';
import { ImpersonationBanner } from '@/src/components/impersonation/impersonation-banner';
import { logoutAction } from '@/src/server/actions/logout.action';
import { ThemeToggle } from './theme-toggle';

const WorkflowRunner = dynamic(
  () =>
    import('@/src/components/onboarding/workflow-runner').then((m) => ({
      default: m.WorkflowRunner,
    })),
  { ssr: false },
);

export function AppChrome({ uiContext, children }: { uiContext: UiContext; children: ReactNode }) {
  const pathname = usePathname();
  useTheme();
  const activeContext = resolveActiveContext(
    pathname,
    uiContext.activeContext,
    uiContext.availableContexts,
  );

  useEffect(() => {
    configureBufferingSink();
  }, []);

  if (uiContext.session?.twoFactorSetupRequired && !isPublicUiPath(pathname)) {
    return (
      <div className="min-h-screen bg-[var(--k-color-bg-app)] text-[var(--k-color-text-primary)]">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] p-4">
          <span className="font-semibold">Kontora · Kontosicherheit</span>
          <div className="flex flex-wrap items-center gap-3">
            <ThemeToggle />
            <form action={logoutAction}>
              <button
                type="submit"
                className="rounded-lg border border-[var(--k-color-border-default)] px-3 py-2 text-sm font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--k-color-border-focus)]"
              >
                Abmelden
              </button>
            </form>
          </div>
        </header>
        {children}
      </div>
    );
  }

  if (isPublicUiPath(pathname) || !uiContext.session) {
    return (
      <GlobalConfirmationProvider>
        <BrandingProvider tenantId={uiContext.session?.activeTenantPublicId ?? 'public'}>
          {children}
          <PwaRegister />
          <InstallPrompt />
          <ToastContainer />
          <ShortcutsCheatsheetMonitor />
        </BrandingProvider>
      </GlobalConfirmationProvider>
    );
  }

  const navigation = resolveNavigation({
    pathname,
    permissions: uiContext.permissions,
    activeFeatures: uiContext.activeFeatures,
    permissionsLoaded: uiContext.permissionsLoaded,
    roles: uiContext.session?.roles ?? [],
    activeContext,
    powerMode: uiContext.powerMode,
  });

  return (
    <GlobalConfirmationProvider>
      <BrandingProvider
        tenantId={uiContext.session?.activeTenantPublicId ?? uiContext.tenants[0]?.id ?? 'default'}
      >
        <div className="k-app-shell" data-tour="app-shell">
          <NavTracker activeContext={activeContext} />
          <AppSidebar
            sections={navigation}
            roleLevel={uiContext.roleLevel}
            roles={uiContext.session?.roles ?? []}
            activeContext={activeContext}
            powerMode={uiContext.powerMode}
          />
          <div className="flex min-w-0 flex-1 flex-col">
            <Topbar
              session={uiContext.session}
              tenants={uiContext.tenants}
              sections={navigation}
              unreadNotifications={uiContext.unreadNotifications}
              roleLevel={uiContext.roleLevel}
              activeContext={activeContext}
              availableContexts={uiContext.availableContexts}
              powerMode={uiContext.powerMode}
              canUsePowerMode={uiContext.canUsePowerMode}
              permissions={uiContext.permissions}
            />
            <main className="min-h-[calc(100vh-7rem)] flex-1 px-4 py-6 pb-[calc(4rem+env(safe-area-inset-bottom))] lg:pb-6 xl:px-8">
              {uiContext.powerMode ? (
                <PowerModeWorkflowsBanner
                  activeContext={activeContext}
                  canUsePowerMode={uiContext.canUsePowerMode}
                />
              ) : null}
              {children}
            </main>
          </div>
          <ErrorBoundary context="WorkflowRunner">
            <WorkflowRunner
              context={activeContext}
              permissions={uiContext.permissions}
              enabled={!uiContext.powerMode}
            />
          </ErrorBoundary>
          <AppShellCommandPalette
            availableContexts={uiContext.availableContexts}
            permissions={uiContext.permissions}
          />
          <SearchPanelMonitor
            sections={uiContext.availableContexts.flatMap((context) =>
              resolveNavigation({
                pathname,
                permissions: uiContext.permissions,
                activeFeatures: uiContext.activeFeatures,
                permissionsLoaded: uiContext.permissionsLoaded,
                roles: uiContext.session?.roles ?? [],
                activeContext: context,
              }),
            )}
          />
          <ShortcutsCheatsheetMonitor />
          <ToastContainer />
          <SessionTimeoutMonitor expiresAt={uiContext.session.expiresAt} />
          <PwaRegister />
          <InstallPrompt />
          <ImpersonationBanner />
          <MobileBottomNav
            activeContext={activeContext}
            availableContexts={uiContext.availableContexts}
          />
        </div>
      </BrandingProvider>
    </GlobalConfirmationProvider>
  );
}
